// ============================================================================
// USER API FUNCTIONS
// ============================================================================

import { getCurrentUserToken, authenticatedFetch } from './authApi';

export interface UserStorageUsage {
  user_id: string;
  total_size_bytes: number;
  total_files: number;
  datasets_count: number;
  total_size_gb: number;
  subscription_limit_gb: number;
  subscription_tier: 'free' | 'pro' | 'enterprise';
  usage_percentage: number;
}

/**
 * Get available models (works for both authenticated and anonymous users)
 */
export const getModels = async (): Promise<any> => {
  const token = await getCurrentUserToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  // Add auth header if user is logged in
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch('/api/models', {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch models');
  }
  
  return response.json();
};

/**
 * Get user profile (requires authentication)
 */
export const getUserProfile = async (): Promise<any> => {
  const response = await authenticatedFetch('/api/user/profile');
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get user profile');
  }
  
  return response.json();
};

/**
 * Initialize user in backend (requires authentication)
 * This should be called after Firebase authentication to create/update user in Firestore
 */
export const initializeUser = async (): Promise<any> => {
  const response = await authenticatedFetch('/api/user/init', {
    method: 'POST',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to initialize user');
  }
  
  return response.json();
};

/**
 * Update user profile (requires authentication)
 * Updates user information like display_name, preferences, etc.
 */
export const updateUserProfile = async (updates: {
  display_name?: string;
  preferences?: Record<string, any>;
}): Promise<any> => {
  const response = await authenticatedFetch('/api/user/profile', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update profile');
  }
  
  return response.json();
};

/**
 * Update subscription tier (requires authentication)
 * Changes user's subscription level (free, pro, enterprise)
 */
export const updateSubscription = async (tier: 'free' | 'pro' | 'enterprise'): Promise<any> => {
  const response = await authenticatedFetch('/api/user/subscription', {
    method: 'PUT',
    body: JSON.stringify({ tier }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update subscription');
  }
  
  return response.json();
};

/**
 * Get user's projects (requires authentication)
 * Returns list of project IDs associated with the user
 */
export const getUserProjects = async (): Promise<any> => {
  const response = await authenticatedFetch('/api/user/projects');
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get user projects');
  }
  
  return response.json();
};

/**
 * Delete user account (requires authentication)
 * WARNING: This permanently deletes the user and all associated data
 */
export const deleteUserAccount = async (): Promise<any> => {
  const response = await authenticatedFetch('/api/user/delete', {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete user account');
  }
  
  return response.json();
};